import { Freshness } from './Freshness';
import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from './BrandLogo';
import { ResourceMark } from './ResourceMark';
import type { ResourceMarkKey } from '../config/resourceMarks';
import { useAccount } from '../contexts/AccountContext';
import { resolveOwnerBrand } from '../config/ownerBrands';
import { resolveBrandTheme } from '../config/brandThemes';
import { StatusBadge } from './StatusBadge';
import { useI18n } from '../i18n/I18nProvider';
import type { LocationWithSummary } from '../hooks/useLocations';
import type { TicketRecord } from '../hooks/useTickets';
import { isOpenStatus } from '../hooks/useTickets';
import {
  UNIFI_EXPECTED_REFRESH_MS,
  UNIFI_STALE_MS,
  type UnifiAggregate,
} from '../hooks/useUnifiSites';
import { directoryLinks } from '../lib/propertyDirectory';
import { PropertyTripList } from './PropertyTripList';
import { usePropertyCatalog } from '../hooks/usePropertyCatalog';
import {
  listDeployedSolutions,
  visitHistoryNote,
  visitsForProperty,
} from '../lib/propertyCatalog';
import { DeployedSolutionList } from './PropertyTripList';

function ExternalArrow() {
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="shrink-0">
      <path d="M7 17 17 7M7 7h10v10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ResourceLink({
  title,
  description,
  href,
  kind,
  newTab,
}: {
  title: string;
  description: string;
  href: string | null;
  kind: ResourceMarkKey;
  newTab: string;
}) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="property-resource-link group flex min-h-11 items-center gap-3 rounded-md border border-border bg-surface-2 px-3 py-2.5 text-primary transition-colors hover:bg-surface-3"
    >
      <ResourceMark kind={kind} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-text">{title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-text-muted">{description}</span>
      </span>
      <ExternalArrow />
      <span className="sr-only">{newTab}</span>
    </a>
  );
}

/**
 * Site detail overlay for the Locations map. Everything shown is joined by
 * id: the location row, its property_directory entry (via
 * hierarchy.property_code), its UniFi aggregate (unifi_site_map.location_id)
 * and its tickets (tickets.location_id / customer_id).
 */
export function PropertyDetailPanel({
  location,
  unifi,
  tickets,
  onClose,
}: {
  location: LocationWithSummary;
  unifi: UnifiAggregate | null;
  tickets: TicketRecord[];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const directory = location.directory;
  const links = directoryLinks(directory);
  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const locationTickets = useMemo(
    () =>
      tickets.filter(
        (ticket) =>
          ticket.location_id === location.id ||
          (ticket.location_id === null && ticket.customer_id === location.customer_id),
      ),
    [tickets, location.id, location.customer_id],
  );
  const openTickets = locationTickets.filter((ticket) => isOpenStatus(ticket.status));

  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
    bodyRef.current?.scrollTo(0, 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [location.id]);

  const { catalog } = usePropertyCatalog();
  const city = location.address?.city ?? directory?.city ?? null;
  const state = location.address?.state ?? directory?.state ?? null;
  const cityState = [city, state].filter(Boolean).join(', ');
  const common = { newTab: t('property.resources.new_tab') };
  const hasOperations = true;
  const hasGuest = !!(links.rankings || links.ordering);
  const hasKnowledge = !!(links.learn || links.engineering);
  const { accounts } = useAccount();
  const ownerBrand = resolveOwnerBrand(accounts.map((a) => a.name));
  // Brand colours for this property, scoped to the panel (the page keeps its own theme).
  const panelBrand = resolveBrandTheme(directory?.brand)?.key;
  // Deployed solutions come from this location's `deployments` rows only; the
  // visit record is read from the registry by directory code.
  const deployedSolutions = listDeployedSolutions((location.deployments ?? []).map((d) => d.solution_type));
  const visits = visitsForProperty(catalog, directory?.code);
  const visitNote = visitHistoryNote(catalog, directory?.code);
  const missingLinks = directory
    ? (
        [
          ['Auvik', links.auvik],
          ['UniFi', links.unifi],
          [t('property.resources.rankings_title'), links.rankings],
          ['Wi-Q', links.ordering],
          ['PazNet Learn', links.learn],
        ] as Array<[string, string | null]>
      )
        .filter(([, href]) => !href)
        .map(([title]) => title)
    : [];

  return (
    <aside
      aria-labelledby="property-panel-title"
      data-brand={panelBrand}
      className="property-detail-panel flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-border bg-surface text-text shadow-2xl"
    >
      <div className="shrink-0 border-b border-border px-4 pb-4 pt-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-text-muted">
            {t('property.resources.site')}
          </p>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="property-resource-link -mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-sm text-text-muted hover:bg-surface-2 hover:text-text"
            aria-label={t('property.resources.close')}
          >
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="m6 6 12 12M18 6 6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="property-panel-title" className="flex items-center gap-2 text-2xl font-semibold leading-tight">
            {ownerBrand && (
              <img src={ownerBrand.logo} alt={ownerBrand.name} className="h-8 w-8 flex-shrink-0 object-contain" />
            )}
            <span>{directory?.code ?? location.name}</span>
          </h2>
          {directory?.brand && <BrandLogo brand={directory.brand} name={location.name} />}
        </div>
        {directory?.code && <p className="mt-3 text-sm font-medium leading-snug">{location.name}</p>}
        {cityState && <p className="mt-1 text-xs text-text-muted">{cityState}</p>}
        <button
          type="button"
          onClick={() => navigate(`/locations/${location.id}`)}
          className="mt-3 inline-flex min-h-11 items-center rounded-md bg-primary px-3 text-sm font-medium text-text-inverse hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:min-h-9"
        >
          {t('locations.open_site')}
        </button>
      </div>

      <div ref={bodyRef} className="min-h-0 space-y-5 overflow-y-auto overscroll-contain px-4 py-4">
        <section aria-labelledby="property-network-heading">
          <h3 id="property-network-heading" className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
            {t('property.resources.network_title')}
          </h3>
          {unifi && unifi.totalDevices !== null ? (
            <dl className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md bg-surface-2 px-2 py-2">
                <dd className="text-lg font-semibold text-text">
                  {unifi.onlineDevices}/{unifi.totalDevices}
                </dd>
                <dt className="text-[11px] text-text-muted">{t('property.resources.devices_online')}</dt>
              </div>
              <div className="rounded-md bg-surface-2 px-2 py-2">
                <dd className="text-lg font-semibold text-text">
                  {(unifi.wifiClients ?? 0) + (unifi.wiredClients ?? 0)}
                </dd>
                <dt className="text-[11px] text-text-muted">{t('property.resources.clients')}</dt>
              </div>
              <div className="rounded-md bg-surface-2 px-2 py-2">
                <dd className="text-lg font-semibold text-text">{unifi.ssids ?? t('common.none')}</dd>
                <dt className="text-[11px] text-text-muted">{t('property.resources.ssids')}</dt>
              </div>
              {/* Invariant 8: these are live counts, so they carry the same
                  freshness contract as every other live surface — a text
                  timestamp alone never turns red when the poll dies. */}
              <div className="col-span-3 flex flex-wrap items-center justify-center gap-2">
                <Freshness
                  lastUpdatedAt={unifi.latestUpdatedAt}
                  label={t('property.resources.network_title')}
                  expectedRefreshMs={UNIFI_EXPECTED_REFRESH_MS}
                  staleMs={UNIFI_STALE_MS}
                />
                <span className="text-[11px] text-text-faint">{t('dashboard.network_source')}</span>
              </div>
            </dl>
          ) : (
            <p className="text-xs text-text-muted">{t('property.resources.network_none')}</p>
          )}
        </section>

        {deployedSolutions.length > 0 && (
          <section aria-labelledby="property-solutions-heading">
            <h3 id="property-solutions-heading" className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
              {t('property.solutions.title')} ({deployedSolutions.length})
            </h3>
            <DeployedSolutionList solutions={deployedSolutions} />
          </section>
        )}

        {visits.length > 0 && (
          <section aria-labelledby="property-trips-heading">
            <h3 id="property-trips-heading" className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
              {t('property.trips.title')} ({visits.length})
            </h3>
            <PropertyTripList
              key={location.id}
              visits={visits}
              note={visitNote}
              earlierLabel={t('property.trips.earlier')}
            />
          </section>
        )}

        <section aria-labelledby="property-tickets-heading">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 id="property-tickets-heading" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
              {t('property.resources.tickets')}
            </h3>
            <button
              type="button"
              onClick={() => navigate(`/tickets?new=1&location=${encodeURIComponent(location.id)}`)}
              className="min-h-11 rounded-sm px-2 text-xs font-medium text-primary hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-8"
            >
              {t('property.resources.new_ticket')}
            </button>
          </div>
          {openTickets.length === 0 ? (
            <p className="text-xs text-text-muted">{t('property.resources.no_open_tickets')}</p>
          ) : (
            <ul className="space-y-2">
              {openTickets.slice(0, 4).map((ticket) => (
                <li key={ticket.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-left hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-text-faint">{ticket.ticket_number}</span>
                      <StatusBadge status={ticket.status ?? 'open'} />
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm font-medium text-text">{ticket.title}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {locationTickets.length > 0 && (
            <button
              type="button"
              onClick={() => navigate('/tickets')}
              className="mt-2 min-h-11 text-xs font-medium text-primary hover:underline sm:min-h-6"
            >
              {t('property.resources.view_all_tickets')}
            </button>
          )}
        </section>

        {!directory && <p className="text-xs text-text-muted">{t('property.resources.no_directory')}</p>}

        {hasOperations && (
          <section aria-labelledby="property-operations-heading">
            <h3 id="property-operations-heading" className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
              {t('property.resources.operations')}
            </h3>
            <div className="space-y-2">
              <ResourceLink title={t('location.tab.network')} kind="missionControl" description={t('property.resources.mission_control')} href={`/locations/${location.id}/network`} {...common} />
              <ResourceLink title="Auvik" kind="auvik" description={t('property.resources.auvik')} href={links.auvik} {...common} />
              <ResourceLink title="UniFi" kind="unifi" description={t('property.resources.unifi')} href={links.unifi} {...common} />
              <ResourceLink title="Pinnacle Communications" kind="communicationsVendor" description={t('property.resources.vendor_website')} href={links.communicationsVendor} {...common} />
            </div>
            {links.additionalNetworks.length > 0 && (
              <details key={location.id} className="mt-3 rounded-md border border-border">
                <summary className="property-resource-link min-h-11 cursor-pointer rounded-md px-3 py-3 text-xs font-medium">
                  {t('property.resources.additional_networks')} ({links.additionalNetworks.length})
                </summary>
                <ul className="space-y-3 border-t border-border p-3">
                  {links.additionalNetworks.map((network) => (
                    <li key={network.name}>
                      <p className="text-xs font-medium">{network.name}</p>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        {network.missionControl && (
                          <a href={`/locations/${location.id}/network`} target="_blank" rel="noopener noreferrer" className="property-resource-link inline-flex min-h-11 items-center gap-1 rounded-sm text-xs font-medium text-primary underline underline-offset-4 sm:min-h-9">
                            {t('location.tab.network')}<ExternalArrow />
                            <span className="sr-only"> · {network.name} · {t('property.resources.new_tab')}</span>
                          </a>
                        )}
                        {network.unifi && (
                          <a href={network.unifi} target="_blank" rel="noopener noreferrer" className="property-resource-link inline-flex min-h-11 items-center gap-1 rounded-sm text-xs font-medium text-primary underline underline-offset-4 sm:min-h-9">
                            UniFi<ExternalArrow />
                            <span className="sr-only"> · {network.name} · {t('property.resources.new_tab')}</span>
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        )}

        {hasGuest && (
          <section aria-labelledby="property-rankings-heading">
            <h3 id="property-rankings-heading" className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
              {t('property.resources.guest_experience')}
            </h3>
            <div className="space-y-2">
              <ResourceLink title={t('property.resources.rankings_title')} kind="rankings" description={t('property.resources.rankings')} href={links.rankings} {...common} />
              <ResourceLink title="Wi-Q" kind="ordering" description={t('property.resources.ordering')} href={links.ordering} {...common} />
            </div>
          </section>
        )}

        {hasKnowledge && (
          <section aria-labelledby="property-knowledge-heading">
            <h3 id="property-knowledge-heading" className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-muted">
              {t('property.resources.knowledge')}
            </h3>
            <div className="space-y-2">
              <ResourceLink title="PazNet Learn" kind="learn" description={t('property.resources.external_learn')} href={links.learn} {...common} />
              <ResourceLink title={t('property.resources.engineering')} kind="engineering" description={t('property.resources.engineering_description')} href={links.engineering} {...common} />
            </div>
          </section>
        )}

        {missingLinks.length > 0 && (
          <details key={`missing-${location.id}`} className="rounded-md border border-dashed border-border">
            <summary className="property-resource-link min-h-11 cursor-pointer rounded-md px-3 py-3 text-xs font-medium">
              {t('property.resources.missing_links')} ({missingLinks.length})
            </summary>
            <div className="border-t border-border px-3 pb-3 pt-2 text-xs text-text-muted">
              <p>{t('property.resources.not_linked')}</p>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                {missingLinks.map((title) => (
                  <li key={title}>{title}</li>
                ))}
              </ul>
            </div>
          </details>
        )}

        {directory && <p className="text-[11px] leading-relaxed text-text-muted">{t('property.resources.sign_in')}</p>}
      </div>
    </aside>
  );
}
